import { useState } from "react";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAdminPlaybooks } from "@/queries/adminPlaybooks";
import {
  useAdminStrategies,
  type AdminStrategy,
} from "@/queries/adminStrategies";

import { StrategyEditDialog } from "./StrategyEditDialog";
import { StrategyRow } from "./StrategyRow";
import { DeleteStrategyDialog } from "./DeleteStrategyDialog";

export function AdminStrategiesPage() {
  const { t } = useTranslation("admin-strategies");
  const { data: strategies, isPending, isError } = useAdminStrategies();
  const { data: playbooks } = useAdminPlaybooks();
  const totalPlaybooks = playbooks?.length ?? 0;

  const [editTarget, setEditTarget] = useState<AdminStrategy | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AdminStrategy | null>(null);

  return (
    <div className="flex flex-col gap-6 p-6">
      <header className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t("page.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("page.subtitle")}</p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" />
          {t("page.newStrategy")}
        </Button>
      </header>

      {isPending ? (
        <p className="text-sm text-muted-foreground">{t("page.loading")}</p>
      ) : isError ? (
        <Alert variant="destructive">
          <AlertDescription>{t("page.loadError")}</AlertDescription>
        </Alert>
      ) : !strategies || strategies.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("page.empty")}</p>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("columns.name")}</TableHead>
                <TableHead>{t("columns.playbookCount")}</TableHead>
                <TableHead className="text-right">
                  {t("columns.dailyBudget")}
                </TableHead>
                <TableHead className="text-right">
                  {t("columns.maxPositionPct")}
                </TableHead>
                <TableHead className="text-right">
                  {t("columns.cooldownHours")}
                </TableHead>
                <TableHead>{t("columns.liveEnabled")}</TableHead>
                <TableHead className="text-right">
                  {t("columns.liveConfidence")}
                </TableHead>
                <TableHead className="text-right">
                  {t("columns.actions")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {strategies.map((row) => (
                <StrategyRow
                  key={row.id}
                  row={row}
                  totalPlaybooks={totalPlaybooks}
                  onEdit={() => setEditTarget(row)}
                  onDelete={() => setDeleteTarget(row)}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {createOpen ? (
        <StrategyEditDialog
          mode="create"
          open
          onOpenChange={(next) => !next && setCreateOpen(false)}
          playbooks={playbooks ?? []}
        />
      ) : null}

      {editTarget ? (
        <StrategyEditDialog
          mode="edit"
          open
          onOpenChange={(next) => !next && setEditTarget(null)}
          playbooks={playbooks ?? []}
          strategy={editTarget}
        />
      ) : null}

      {deleteTarget ? (
        <DeleteStrategyDialog
          open
          onOpenChange={(next) => !next && setDeleteTarget(null)}
          strategy={deleteTarget}
        />
      ) : null}
    </div>
  );
}
